import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Info,
  FileSpreadsheet,
  FileText,
  ExternalLink,
  Table2,
  Calendar,
  Building2,
  MapPin,
  DollarSign,
  Trash2,
  Filter,
  X,
} from 'lucide-react';
import {
  companySettingsApi,
  payrollRunApi,
} from '../services/api';
import {
  freqKindDisplayLabel,
  getEnabledPayrollCycleOptions,
  type PayrollCycleOption,
} from '../utils/payrollCycles';
import { appConfirm } from '../context/SystemDialogContext';
import {
  loadPayrollReportEmployees,
  loadPayrollReportPayRecords,
  loadPayrollReportReferenceData,
} from '../utils/payrollReportBootstrap';
import BasketSelect from '../components/BasketSelect';
import { usePermissions } from '../hooks/usePermissions';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { WeekPickerCalendar } from '../components/payroll/WeekPickerCalendar';
import {
  parseLocalDate,
  toLocalDateString,
  formatRangeDate,
  getPeriodEndForKind,
  normalizeFreqName,
  getOohHourlyCycleContaining,
} from '../utils/payrollPeriodLocal';
import {
  downloadPayrollRegisterCsv,
  downloadPayrollRegisterExcel,
  downloadPayrollRegisterPdf,
} from '../utils/payrollRunExport';
import { formatPayrollLineDetail, formatPayrollRunPeriodRow } from '../utils/payrollReportOutputCatalog';
import { formatCompanyDateLocale, formatCompanyDateTime } from '../utils/companyDateFormat';
import type {
  PayrollRun,
  PayrollRunPreviewDto,
  PayrollRunPreviewFiltersSnapshot,
  EmployeePayrollCalculationResultDto,
  PayrollGrossLineDto,
  PaymentFrequency,
  PayrollDepartment,
  Location,
  Employee,
  PayRecord,
  Schedule,
  Position,
} from '../types';

function formatYmd(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type FreqKind = 'weekly' | 'monthly' | 'fortnightly' | 'ooh-hourly';
type PayCategoryFilter = 'both' | 'hourly' | 'salaried';

function payCategoryDisplay(kind: PayCategoryFilter): string {
  switch (kind) {
    case 'hourly':
      return 'Hourly only';
    case 'salaried':
      return 'Salaried only';
    default:
      return 'Hourly and salaried';
  }
}

function resolvePaymentFrequencyIds(freqs: PaymentFrequency[]) {
  const norm = (s: string) => normalizeFreqName(s);
  const weekly = freqs.find((f) => {
    const n = norm(f.name);
    return n === 'weekly' || n === 'week';
  });
  const monthly = freqs.find((f) => {
    const n = norm(f.name);
    return n === 'monthly' && !n.includes('invoice');
  });
  const fort = freqs.find((f) => {
    const n = norm(f.name);
    return n.includes('fortnight') || n.includes('bi-week') || n.includes('biweek');
  });
  return { weeklyId: weekly?.id ?? null, monthlyId: monthly?.id ?? null, fortnightlyId: fort?.id ?? null };
}

function freqKindDisplay(kind: PayrollRunPreviewFiltersSnapshot['freqKind']): string {
  return freqKindDisplayLabel(kind);
}

function parseSavedFiltersJson(json: string): PayrollRunPreviewFiltersSnapshot | null {
  try {
    const o = JSON.parse(json) as unknown;
    if (o && typeof o === 'object' && o !== null && 'freqKind' in o) {
      return o as PayrollRunPreviewFiltersSnapshot;
    }
  } catch {
    return null;
  }
  return null;
}

function filterPayrollRunWarnings(warnings: string[], isDraftPreview: boolean): string[] {
  if (!isDraftPreview) return warnings;
  return warnings.filter((w) => {
    const lower = w.toLowerCase();
    if (lower.includes('gross is zero')) return false;
    if (lower.includes('no approved timecard days')) return false;
    if (w.includes('PD-013')) return false;
    if (w.includes('PD-015')) return false;
    if (w.includes('PD-018')) return false;
    if (lower.includes('approved leave day')) return false;
    if (lower.includes('legacy cutover')) return false;
    if (lower.includes('paid leave does not add gross')) return false;
    return true;
  });
}

const PAYROLL_LINE_CATEGORY_ORDER: Record<string, number> = {
  MatrixRate: 0,
  SundayPremium: 1,
  BankHolidayPay: 2,
  WeekSubtotal: 3,
};

function sortPayrollPreviewLines(lines: PayrollGrossLineDto[]): PayrollGrossLineDto[] {
  return [...lines].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    if (da !== db) return da - db;
    const oa = PAYROLL_LINE_CATEGORY_ORDER[a.category] ?? 99;
    const ob = PAYROLL_LINE_CATEGORY_ORDER[b.category] ?? 99;
    if (oa !== ob) return oa - ob;
    return (a.detail || '').localeCompare(b.detail || '');
  });
}

function displayLineAmount(ln: PayrollGrossLineDto): number {
  if (ln.category === 'MatrixRate' && Number(ln.amount) === 0) {
    return Number(ln.hours) * Number(ln.hourlyRateApplied);
  }
  return Number(ln.amount);
}

function weekSubtotalTotals(
  sorted: PayrollGrossLineDto[],
  weekDate: string | null | undefined
): { hours: number; amount: number } {
  const weekRows = sorted.filter(
    (row) =>
      row.date === weekDate &&
      row.category !== 'WeekSubtotal' &&
      PAYROLL_LINE_CATEGORY_ORDER[row.category] != null
  );
  return {
    hours: weekRows.reduce((sum, row) => sum + Number(row.hours), 0),
    amount: weekRows.reduce((sum, row) => sum + displayLineAmount(row), 0),
  };
}

const PayrollRunsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canForce = hasPermission('payroll_management');
  const weekStartsOn = useWeekStartDay();

  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [preview, setPreview] = useState<PayrollRunPreviewDto | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [createNotes, setCreateNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const [payrollDepartments, setPayrollDepartments] = useState<PayrollDepartment[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payRecords, setPayRecords] = useState<PayRecord[]>([]);
  const [freqIds, setFreqIds] = useState<{
    weeklyId: number | null;
    monthlyId: number | null;
    fortnightlyId: number | null;
  }>({ weeklyId: null, monthlyId: null, fortnightlyId: null });
  const [freqKind, setFreqKind] = useState<FreqKind>('weekly');
  const [enabledCycles, setEnabledCycles] = useState<PayrollCycleOption[]>(() => getEnabledPayrollCycleOptions());
  const [payCategoryFilter, setPayCategoryFilter] = useState<PayCategoryFilter>('both');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [locationId, setLocationId] = useState<number | ''>('');
  const [scheduleId, setScheduleId] = useState<number | ''>('');
  const [positionId, setPositionId] = useState<number | ''>('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodMonth, setPeriodMonth] = useState('');
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [fortnightPickerOpen, setFortnightPickerOpen] = useState(false);
  const [hourlyCyclePickerOpen, setHourlyCyclePickerOpen] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(true);
  const [employeeMasterLoading, setEmployeeMasterLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeMessage, setFinalizeMessage] = useState<string | null>(null);
  const [forceFinalize, setForceFinalize] = useState(false);
  const [exportLoading, setExportLoading] = useState<'csv' | 'xlsx' | 'pdf' | 'ssp' | 'entity' | null>(null);
  const [varianceLoading, setVarianceLoading] = useState(false);
  const [varianceSummary, setVarianceSummary] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [filtersModalJson, setFiltersModalJson] = useState<string | null>(null);
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>([]);

  const loadRuns = useCallback(async (signal?: AbortSignal) => {
    setLoadingList(true);
    try {
      const list = await payrollRunApi.list(80);
      if (signal?.aborted) return;
      setRuns(list);
      setSelectedId((sid) => {
        if (sid != null && !list.some((r) => r.id === sid)) return null;
        return sid;
      });
    } catch (e: unknown) {
      if (signal?.aborted) return;
      console.error(e);
    } finally {
      if (!signal?.aborted) setLoadingList(false);
    }
  }, []);

  const handleDeleteRun = async (e: React.MouseEvent, r: PayrollRun) => {
    e.stopPropagation();
    setDeleteError(null);
    if (r.status?.toLowerCase() !== 'draft') return;
    if (!(await appConfirm(`Delete draft payroll run #${r.id}? This cannot be undone.`, { danger: true, confirmLabel: 'Delete' }))) return;
    setDeletingId(r.id);
    try {
      await payrollRunApi.delete(r.id);
      if (selectedId === r.id) {
        setSelectedId(null);
        setPreview(null);
        setPreviewError(null);
      }
      await loadRuns();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setDeleteError(msg || 'Failed to delete payroll run.');
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    void loadRuns(controller.signal);
    return () => controller.abort();
  }, [loadRuns]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (filtersModalJson != null) setFiltersModalJson(null);
      else if (selectedId != null) setSelectedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtersModalJson, selectedId]);

  const getWeekStart = useCallback(
    (date: Date): string => {
      const day = date.getDay();
      let diff = day - weekStartsOn;
      if (diff < 0) diff += 7;
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - diff);
      return toLocalDateString(weekStart);
    },
    [weekStartsOn]
  );

  const fortnightlyDateOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const today = new Date();
    for (let m = -24; m <= 12; m++) {
      const base = new Date(today.getFullYear(), today.getMonth() + m, 1);
      for (const dayRangeStart of [1, 15]) {
        const endDay = Math.min(
          dayRangeStart + 6,
          new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate()
        );
        for (let day = dayRangeStart; day <= endDay; day++) {
          const d = new Date(base.getFullYear(), base.getMonth(), day);
          if (d.getDay() === weekStartsOn) {
            const ymd = toLocalDateString(d);
            options.push({
              value: ymd,
              label: formatCompanyDateLocale(d, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }),
            });
            break;
          }
        }
      }
    }
    return options;
  }, [weekStartsOn]);

  const effectivePeriodStart = useMemo(() => {
    if (freqKind === 'ooh-hourly') {
      if (periodStartDate) return getOohHourlyCycleContaining(parseLocalDate(periodStartDate)).start;
      return getOohHourlyCycleContaining(new Date()).start;
    }
    if (freqKind === 'monthly' && periodMonth) return `${periodMonth}-01`;
    return periodStartDate;
  }, [freqKind, periodMonth, periodStartDate]);

  const effectivePeriodEnd = useMemo(() => {
    if (!effectivePeriodStart) return '';
    return getPeriodEndForKind(effectivePeriodStart, freqKind);
  }, [effectivePeriodStart, freqKind]);

  /** Single human-readable run period (avoids duplicating “Period:” under the picker and “Run period:” below). */
  const runPeriodDisplayLabel = useMemo(() => {
    if (!effectivePeriodStart || !effectivePeriodEnd) return '';
    const a = parseLocalDate(effectivePeriodStart);
    const b = parseLocalDate(effectivePeriodEnd);
    return `${formatRangeDate(a)} – ${formatRangeDate(b)}`;
  }, [effectivePeriodStart, effectivePeriodEnd]);

  const selectedFrequencyIdForFilter = useMemo(() => {
    if (freqKind === 'weekly' || freqKind === 'ooh-hourly') return freqIds.weeklyId;
    if (freqKind === 'monthly') return freqIds.monthlyId;
    return freqIds.fortnightlyId;
  }, [freqKind, freqIds]);

  const weekRangeLabel = useMemo(() => {
    if (!periodStartDate || freqKind !== 'weekly') return '';
    const start = parseLocalDate(periodStartDate);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [periodStartDate, freqKind]);

  const fortnightRangeLabel = useMemo(() => {
    if (!periodStartDate || freqKind !== 'fortnightly') return '';
    const start = parseLocalDate(periodStartDate);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 13);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [periodStartDate, freqKind]);

  const monthRangeLabel = useMemo(() => {
    if (!periodMonth || freqKind !== 'monthly') return '';
    const [y, mo] = periodMonth.split('-').map(Number);
    const start = new Date(y, mo - 1, 1);
    const end = new Date(y, mo, 0);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [periodMonth, freqKind]);

  const periodSelectionLabelForSnapshot = useMemo(() => {
    if (freqKind === 'weekly') return weekRangeLabel || null;
    if (freqKind === 'fortnightly') return fortnightRangeLabel || null;
    if (freqKind === 'monthly') return monthRangeLabel || null;
    return null;
  }, [freqKind, weekRangeLabel, fortnightRangeLabel, monthRangeLabel]);

  const employeeMatchesScheduleFilter = useCallback(
    (emp: EmployeePayrollCalculationResultDto, filterScheduleId: number) => {
      const pr = payRecords.find((p) => p.employeeId === emp.employeeId && p.isActive);
      if (pr?.scheduleId === filterScheduleId) return true;
      const scheduleName = schedules.find((s) => s.id === filterScheduleId)?.name;
      if (!scheduleName) return false;
      return (emp.lines || []).some((ln) => (ln.detail || '').includes(scheduleName));
    },
    [payRecords, schedules]
  );

  const employeeMatchesPositionFilter = useCallback(
    (emp: EmployeePayrollCalculationResultDto, filterPositionId: number) => {
      const pr = payRecords.find((p) => p.employeeId === emp.employeeId && p.isActive);
      return pr?.positionId === filterPositionId;
    },
    [payRecords]
  );

  /** Preview-only filters. Location uses EmployeeProfile.mainLocationId only — not timecard day location. */
  const filteredPreviewEmployees = useMemo(() => {
    if (!preview?.employees) return [];
    const filtered = preview.employees.filter((emp) => {
      const empRow = employees.find((e) => e.id === emp.employeeId);
      const profile = empRow?.profile;
      const pr = payRecords.find((p) => p.employeeId === emp.employeeId && p.isActive);
      if (departmentId !== '' && profile?.payrollDepartmentId !== departmentId) return false;
      if (locationId !== '') {
        const mainLoc = profile?.mainLocationId;
        if (mainLoc !== locationId) return false;
      }
      if (scheduleId !== '' && !employeeMatchesScheduleFilter(emp, scheduleId)) return false;
      if (positionId !== '' && !employeeMatchesPositionFilter(emp, positionId)) return false;
      if (selectedEmployeeIds.length > 0 && !selectedEmployeeIds.includes(emp.employeeId)) return false;
      if (selectedFrequencyIdForFilter != null && pr?.paymentFrequencyId !== selectedFrequencyIdForFilter)
        return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const aPaid = a.grossTotal > 0 ? 1 : 0;
      const bPaid = b.grossTotal > 0 ? 1 : 0;
      if (bPaid !== aPaid) return bPaid - aPaid;
      return a.employeeName.localeCompare(b.employeeName, undefined, { sensitivity: 'base' });
    });
  }, [
    preview,
    employees,
    payRecords,
    departmentId,
    locationId,
    scheduleId,
    positionId,
    selectedEmployeeIds,
    selectedFrequencyIdForFilter,
    employeeMatchesScheduleFilter,
    employeeMatchesPositionFilter,
  ]);

  useEffect(() => {
    if (enabledCycles.length > 0 && !enabledCycles.some((c) => c.kind === freqKind)) {
      setFreqKind(enabledCycles[0].kind as FreqKind);
    }
  }, [enabledCycles, freqKind]);

  useEffect(() => {
    if (freqKind === 'ooh-hourly') setPayCategoryFilter('hourly');
    else if (freqKind === 'monthly') setPayCategoryFilter('salaried');
  }, [freqKind]);

  useEffect(() => {
    let cancelled = false;
    const loadReference = async () => {
      setReferenceLoading(true);
      try {
        const [ref, companySettings] = await Promise.all([
          loadPayrollReportReferenceData(),
          companySettingsApi.get().catch(() => null),
        ]);
        if (cancelled) return;
        setPayrollDepartments(ref.payrollDepartments);
        setLocations(ref.locations);
        setSchedules(ref.schedules);
        setPositions(ref.positions);
        setPaymentFrequencies(ref.paymentFrequencies);
        setFreqIds(resolvePaymentFrequencyIds(ref.paymentFrequencies));
        setEnabledCycles(getEnabledPayrollCycleOptions(companySettings));
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setReferenceLoading(false);
      }
    };
    void loadReference();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadEmployeeMaster = async () => {
      setEmployeeMasterLoading(true);
      try {
        const [emps, records] = await Promise.all([
          loadPayrollReportEmployees(),
          loadPayrollReportPayRecords(),
        ]);
        if (cancelled) return;
        setEmployees(emps || []);
        setPayRecords(records || []);
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setEmployeeMasterLoading(false);
      }
    };
    void loadEmployeeMaster();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const today = new Date();
    setPeriodStartDate(getWeekStart(today));
    setPeriodMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  }, [getWeekStart]);

  const skipFreqKindReset = useRef(true);
  useEffect(() => {
    if (skipFreqKindReset.current) {
      skipFreqKindReset.current = false;
      return;
    }
    setWeekPickerOpen(false);
    setFortnightPickerOpen(false);
    setHourlyCyclePickerOpen(false);
    const today = new Date();
    if (freqKind === 'monthly') {
      setPeriodMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
    } else if (freqKind === 'fortnightly') {
      const ws = getWeekStart(today);
      const nearest =
        fortnightlyDateOptions.find((o) => o.value >= ws) ||
        fortnightlyDateOptions[fortnightlyDateOptions.length - 1];
      if (nearest) setPeriodStartDate(nearest.value);
    } else {
      setPeriodStartDate(getWeekStart(today));
    }
  }, [freqKind, getWeekStart, fortnightlyDateOptions]);

  const loadPreview = useCallback(async (runId: number, signal?: AbortSignal) => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const p = await payrollRunApi.preview(runId);
      if (signal?.aborted) return;
      setPreview(p);
    } catch (e: unknown) {
      if (signal?.aborted) return;
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      setPreviewError(msg || 'Failed to load preview.');
      setPreview(null);
    } finally {
      if (!signal?.aborted) setLoadingPreview(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId == null) {
      setPreview(null);
      setPreviewError(null);
      setLoadingPreview(false);
      return;
    }

    const controller = new AbortController();
    void loadPreview(selectedId, controller.signal);
    return () => controller.abort();
  }, [selectedId, loadPreview]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectivePeriodStart || !effectivePeriodEnd) return;
    setCreating(true);
    setFinalizeMessage(null);
    try {
      const snapshot: PayrollRunPreviewFiltersSnapshot = {
        freqKind,
        periodSelectionLabel: periodSelectionLabelForSnapshot,
        payrollDepartmentId: departmentId === '' ? null : departmentId,
        payrollDepartmentName:
          departmentId === '' ? null : payrollDepartments.find((d) => d.id === departmentId)?.name ?? null,
        mainLocationId: locationId === '' ? null : locationId,
        mainLocationName: locationId === '' ? null : locations.find((l) => l.id === locationId)?.name ?? null,
        paymentFrequencyId: selectedFrequencyIdForFilter,
        paymentFrequencyName:
          selectedFrequencyIdForFilter != null
            ? paymentFrequencies.find((f) => f.id === selectedFrequencyIdForFilter)?.name ?? null
            : null,
        payCategoryFilter,
        scheduleId: scheduleId === '' ? null : scheduleId,
        scheduleName:
          scheduleId === '' ? null : schedules.find((s) => s.id === scheduleId)?.name ?? null,
        positionId: positionId === '' ? null : positionId,
        positionName:
          positionId === '' ? null : positions.find((p) => p.id === positionId)?.title ?? null,
        employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : null,
      };
      const run = await payrollRunApi.create({
        periodStart: effectivePeriodStart,
        periodEnd: effectivePeriodEnd,
        notes: createNotes.trim() || undefined,
        previewFiltersJson: JSON.stringify(snapshot),
      });
      setCreateNotes('');
      await loadRuns();
      setSelectedId(run.id);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Could not create run.';
      setFinalizeMessage(msg || 'Could not create run.');
    } finally {
      setCreating(false);
    }
  };

  const handleFinalize = async () => {
    if (selectedId == null || !preview) return;
    if (forceFinalize && !canForce) return;
    if (forceFinalize && !preview.hasHourlyMismatchBlocker) return;

    setFinalizing(true);
    setFinalizeMessage(null);
    try {
      await payrollRunApi.finalize(selectedId, forceFinalize && preview.hasHourlyMismatchBlocker);
      setForceFinalize(false);
      await loadRuns();
      await loadPreview(selectedId);
      setFinalizeMessage('Run finalized.');
    } catch (e: unknown) {
      const status = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { status?: number; data?: { message?: string } } }).response?.status
        : undefined;
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      if (status === 403) {
        setFinalizeMessage('You do not have permission to force-finalize (payroll_management).');
      } else {
        setFinalizeMessage(msg || 'Finalize failed.');
      }
    } finally {
      setFinalizing(false);
    }
  };

  const toggleEmp = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedRun = runs.find((r) => r.id === selectedId);

  const runPayrollExport = (kind: 'csv' | 'xlsx' | 'pdf') => {
    if (selectedId == null || !preview || !selectedRun) return;
    const employees = filteredPreviewEmployees;
    if (employees.length === 0) {
      setFinalizeMessage('No employees to export for the current filters.');
      return;
    }
    setExportLoading(kind);
    setFinalizeMessage(null);
    try {
      const opts = {
        run: selectedRun,
        employees,
        isFilteredView: employees.length !== preview.employees.length,
        totalEmployeesInPreview: preview.employees.length,
      };
      if (kind === 'csv') downloadPayrollRegisterCsv(opts);
      else if (kind === 'xlsx') downloadPayrollRegisterExcel(opts);
      else downloadPayrollRegisterPdf(opts);
    } catch (e) {
      console.error(e);
      setFinalizeMessage('Export failed.');
    } finally {
      setExportLoading(null);
    }
  };

  const runEntitySplitCsvExport = () => {
    if (selectedId == null || !preview || !selectedRun) return;
    if (preview.employees.length === 0) {
      setFinalizeMessage('No employees to export.');
      return;
    }
    setExportLoading('entity');
    setFinalizeMessage(null);
    try {
      const byLocation = new Map<number | 'none', typeof preview.employees>();
      for (const emp of preview.employees) {
        const master = employees.find((e) => e.id === emp.employeeId);
        const locId = master?.profile?.mainLocationId ?? 'none';
        const list = byLocation.get(locId) ?? [];
        list.push(emp);
        byLocation.set(locId, list);
      }
      let files = 0;
      for (const [locId, emps] of Array.from(byLocation.entries())) {
        const locName =
          locId === 'none'
            ? 'NoLocation'
            : locations.find((l) => l.id === locId)?.name?.replace(/[^\w\-]+/g, '_') || `Location${locId}`;
        downloadPayrollRegisterCsv({
          run: selectedRun,
          employees: emps,
          isFilteredView: true,
          totalEmployeesInPreview: preview.employees.length,
          fileNameSuffix: locName,
        });
        files += 1;
      }
      setFinalizeMessage(`Exported ${files} CSV file(s) split by main location (entity partition).`);
    } catch (e) {
      console.error(e);
      setFinalizeMessage('Entity-split export failed.');
    } finally {
      setExportLoading(null);
    }
  };

  const runSickSspCsvExport = async () => {
    if (selectedId == null) return;
    setExportLoading('ssp');
    setFinalizeMessage(null);
    try {
      const blob = await payrollRunApi.exportSickPaySspCsv(selectedId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sick-pay-ssp-run-${selectedId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setFinalizeMessage('Sick pay SSP CSV export failed.');
    } finally {
      setExportLoading(null);
    }
  };

  const loadVariance = async () => {
    if (selectedId == null) return;
    setVarianceLoading(true);
    setVarianceSummary(null);
    try {
      const v = await payrollRunApi.variance(selectedId);
      setVarianceSummary(
        `${v.projectedLabel} → ${v.actualLabel}: Δ €${v.totalDelta.toFixed(2)} (projected €${v.totalProjected.toFixed(2)}, actual €${v.totalActual.toFixed(2)}, ${v.rows.length} employees with differences or amounts).`
      );
    } catch (e) {
      console.error(e);
      setVarianceSummary('Variance report failed.');
    } finally {
      setVarianceLoading(false);
    }
  };

  const canExportList = Boolean(
    selectedId != null && preview && selectedRun && filteredPreviewEmployees.length > 0
  );

  return (
    <div className="w-full max-w-none flex flex-col flex-1 min-h-0 min-w-0 h-full px-3 sm:px-4 lg:px-6 py-3 lg:py-4 space-y-3">
      <div>
        <h1 className="text-xl font-semibold text-neutral-900">Payroll runs</h1>
        <p className="text-xs text-neutral-600 mt-0.5 leading-snug">
          Draft, preview gross (PD-010 etc.), finalize. Force-finalize needs{' '}
          <code className="text-[10px] bg-neutral-100 px-1 rounded">payroll_management</code>. Salary lines use pay-record proration (PD-008/014).
        </p>
      </div>

      <form
        onSubmit={handleCreate}
        className="bg-white border border-neutral-200 rounded-lg p-3 shadow-sm space-y-2.5"
      >
        <h2 className="text-sm font-semibold text-neutral-900">New draft run</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-3 gap-y-2">
          <div className="space-y-1 min-w-0 sm:col-span-2 xl:col-span-1">
            <label className="block text-xs font-medium text-neutral-700">
              <DollarSign className="inline h-3.5 w-3.5 mr-0.5 text-neutral-500 align-text-bottom" aria-hidden />
              Frequency
            </label>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {enabledCycles.length === 0 ? (
                <p className="text-[11px] text-amber-800">
                  No payroll cycles enabled. Configure under Settings → Company Settings → Payroll Cycles.
                </p>
              ) : (
                enabledCycles.map(({ kind, label }) => (
                  <label key={kind} className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-neutral-800">
                    <input
                      type="radio"
                      name="payrollRunFreq"
                      checked={freqKind === kind}
                      onChange={() => setFreqKind(kind)}
                      className="text-blue-600 border-neutral-300"
                    />
                    {label}
                  </label>
                ))
              )}
            </div>
            {(freqKind === 'weekly' && freqIds.weeklyId == null) ||
            (freqKind === 'monthly' && freqIds.monthlyId == null) ||
            (freqKind === 'fortnightly' && freqIds.fortnightlyId == null) ? (
              <p className="text-[11px] text-amber-800 leading-tight">
                Add Weekly / Monthly / Fortnightly in payment frequencies.
              </p>
            ) : null}
          </div>

          <div className="space-y-1 min-w-0">
            <label className="block text-xs font-medium text-neutral-700">
              <Building2 className="inline h-3.5 w-3.5 mr-0.5 text-neutral-500 align-text-bottom" aria-hidden />
              Department
            </label>
            <select
              value={departmentId === '' ? '' : String(departmentId)}
              onChange={(e) =>
                setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))
              }
              className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-xs bg-white"
            >
              <option value="">All departments</option>
              {payrollDepartments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1 min-w-0">
            <label className="block text-xs font-medium text-neutral-700">
              <MapPin className="inline h-3.5 w-3.5 mr-0.5 text-neutral-500 align-text-bottom" aria-hidden />
              Main location
            </label>
            <select
              value={locationId === '' ? '' : String(locationId)}
              onChange={(e) => setLocationId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-xs bg-white"
              aria-describedby="payroll-run-main-location-hint"
              title="Preview filter: profile main location only"
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            <p id="payroll-run-main-location-hint" className="sr-only">
              List filter uses profile main location only.
            </p>
          </div>
          <div className="space-y-1 min-w-0">
            <label className="block text-xs font-medium text-neutral-700">Schedule</label>
            <select
              value={scheduleId === '' ? '' : String(scheduleId)}
              onChange={(e) => setScheduleId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-xs bg-white"
            >
              <option value="">All schedules</option>
              {schedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1 min-w-0">
            <label className="block text-xs font-medium text-neutral-700">Position</label>
            <select
              value={positionId === '' ? '' : String(positionId)}
              onChange={(e) => setPositionId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-xs bg-white"
            >
              <option value="">All positions</option>
              {positions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1 min-w-0 sm:col-span-2">
            <BasketSelect
              label="Employees"
              availableItems={employees
                .filter((e) => e.isActive)
                .map((e) => ({
                  id: e.id,
                  label: `${e.firstName} ${e.lastName}`.trim() || `#${e.id}`,
                }))}
              selectedIds={selectedEmployeeIds}
              onChange={(ids) =>
                setSelectedEmployeeIds(ids.map((id) => (typeof id === 'string' ? Number(id) : id)))
              }
              placeholder="All employees (optional)"
            />
          </div>

          <div className="space-y-1 min-w-0 sm:col-span-2 xl:col-span-1">
            <label className="block text-xs font-medium text-neutral-700">Pay category</label>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(
                [
                  { kind: 'both' as const, label: 'Both' },
                  { kind: 'hourly' as const, label: 'Hourly' },
                  { kind: 'salaried' as const, label: 'Salaried' },
                ] as const
              ).map(({ kind, label }) => (
                <label key={kind} className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-neutral-800">
                  <input
                    type="radio"
                    name="payrollRunPayCategory"
                    checked={payCategoryFilter === kind}
                    onChange={() => setPayCategoryFilter(kind)}
                    className="text-blue-600 border-neutral-300"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-end">
          <div className="lg:col-span-4 space-y-1 min-w-0">
            {freqKind === 'monthly' ? (
              <>
                <label className="block text-xs font-medium text-neutral-700">
                  <Calendar className="inline h-3.5 w-3.5 mr-0.5 text-neutral-500 align-text-bottom" aria-hidden />
                  Salaried month
                </label>
                <input
                  type="month"
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(e.target.value)}
                  className="w-full max-w-xs px-2 py-1.5 border border-neutral-300 rounded-md text-xs"
                />
              </>
            ) : freqKind === 'ooh-hourly' ? (
              <>
                <label className="block text-xs font-medium text-neutral-700">
                  <Calendar className="inline h-3.5 w-3.5 mr-0.5 text-neutral-500 align-text-bottom" aria-hidden />
                  Hourly 29–28 cycle
                </label>
                {hourlyCyclePickerOpen ? (
                  <WeekPickerCalendar
                    value={periodStartDate}
                    onChange={(ymd) => setPeriodStartDate(ymd)}
                    onClose={() => setHourlyCyclePickerOpen(false)}
                    weekStartsOn={weekStartsOn}
                    resolvePeriodStart={(date) => getOohHourlyCycleContaining(date).start}
                    isDayInSelection={(date, selectedStart) => {
                      const cycle = getOohHourlyCycleContaining(parseLocalDate(selectedStart));
                      const ymd = toLocalDateString(date);
                      return ymd >= cycle.start && ymd <= cycle.end;
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setHourlyCyclePickerOpen(true)}
                    className="flex w-full max-w-sm items-center gap-1.5 px-2 py-1.5 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-xs"
                  >
                    <Calendar className="h-3.5 w-3.5 text-neutral-500 shrink-0" aria-hidden />
                    <span className="text-neutral-800">
                      {runPeriodDisplayLabel || 'Select 29–28 cycle'}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-neutral-400 ml-auto shrink-0" aria-hidden />
                  </button>
                )}
              </>
            ) : freqKind === 'fortnightly' ? (
              <>
                <label className="block text-xs font-medium text-neutral-700">
                  <Calendar className="inline h-3.5 w-3.5 mr-0.5 text-neutral-500 align-text-bottom" aria-hidden />
                  Fortnight
                </label>
                {fortnightPickerOpen ? (
                  <select
                    value={periodStartDate}
                    onChange={(e) => {
                      setPeriodStartDate(e.target.value);
                      setFortnightPickerOpen(false);
                    }}
                    className="w-full max-w-xs px-2 py-1.5 border border-neutral-300 rounded-md text-xs bg-white"
                    autoFocus
                  >
                    {fortnightlyDateOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <button
                    type="button"
                    onClick={() => setFortnightPickerOpen(true)}
                    className="flex w-full max-w-sm items-center gap-1.5 px-2 py-1.5 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-xs"
                  >
                    <Calendar className="h-3.5 w-3.5 text-neutral-500 shrink-0" aria-hidden />
                    <span className={fortnightRangeLabel ? 'text-neutral-800' : 'text-neutral-500'}>
                      {fortnightRangeLabel || 'Select fortnight'}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-neutral-400 ml-auto shrink-0" aria-hidden />
                  </button>
                )}
              </>
            ) : (
              <>
                <label className="block text-xs font-medium text-neutral-700">
                  <Calendar className="inline h-3.5 w-3.5 mr-0.5 text-neutral-500 align-text-bottom" aria-hidden />
                  Week
                </label>
                {weekPickerOpen ? (
                  <WeekPickerCalendar
                    value={periodStartDate}
                    onChange={(ymd) => setPeriodStartDate(ymd)}
                    onClose={() => setWeekPickerOpen(false)}
                    weekStartsOn={weekStartsOn}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setWeekPickerOpen(true)}
                    className="flex w-full max-w-sm items-center gap-1.5 px-2 py-1.5 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-xs"
                  >
                    <Calendar className="h-3.5 w-3.5 text-neutral-500 shrink-0" aria-hidden />
                    <span className={weekRangeLabel ? 'text-neutral-800' : 'text-neutral-500'}>
                      {weekRangeLabel || 'Select week'}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-neutral-400 ml-auto shrink-0" aria-hidden />
                  </button>
                )}
              </>
            )}
          </div>

          <div className="lg:col-span-5 space-y-1 min-w-0">
            <label className="block text-xs font-medium text-neutral-700">Notes (optional)</label>
            <input
              type="text"
              className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-xs"
              value={createNotes}
              onChange={(ev) => setCreateNotes(ev.target.value)}
              placeholder="e.g. March payroll"
            />
          </div>

          <div className="lg:col-span-3 flex justify-end">
            <button
              type="submit"
              disabled={
                creating ||
                referenceLoading ||
                enabledCycles.length === 0 ||
                !effectivePeriodStart ||
                !effectivePeriodEnd
              }
              className="w-full sm:w-auto px-4 py-1.5 bg-neutral-900 text-white text-xs font-medium rounded-md hover:bg-neutral-800 disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create draft'}
            </button>
          </div>
        </div>

        {runPeriodDisplayLabel ? (
          <div
            className="rounded-md bg-neutral-50 border border-neutral-200/80 px-2.5 py-1.5 text-xs text-neutral-800"
            title={
              effectivePeriodStart && effectivePeriodEnd
                ? `Stored as ${effectivePeriodStart} → ${effectivePeriodEnd} (inclusive)`
                : undefined
            }
          >
            <span className="font-medium text-neutral-900">Period:</span> {runPeriodDisplayLabel}
          </div>
        ) : null}
      </form>

      <section className="bg-white border border-neutral-200 rounded-lg p-3 shadow-sm flex flex-col w-full flex-1 min-h-0">
        <div className="flex flex-wrap items-start justify-between gap-2 shrink-0">
          <div>
            <h2 className="text-sm font-medium text-neutral-800">Run history</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Click a row to open the payroll list in a window.</p>
          </div>
          <button
            type="button"
            onClick={() => void loadRuns()}
            className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        {deleteError && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{deleteError}</div>
        )}
        {loadingList ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm py-4">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : runs.length === 0 ? (
          <p className="text-sm text-neutral-500">No payroll runs yet.</p>
        ) : (
          <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 -mx-1 px-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-200">
                  <th className="pb-2 pr-3">ID</th>
                  <th className="pb-2 pr-3">Pay period</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3">Notes</th>
                  <th className="pb-2 pr-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      <Filter className="w-3.5 h-3.5" aria-hidden />
                      Filter
                    </span>
                  </th>
                  <th className="pb-2 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 ${
                      selectedId === r.id ? 'bg-amber-50/60' : ''
                    }`}
                    onClick={() => setSelectedId(r.id)}
                  >
                    <td className="py-2 pr-3 font-mono">{r.id}</td>
                    <td className="py-2 pr-3">{formatPayrollRunPeriodRow(r)}</td>
                    <td className="py-2 pr-3">
                      <span
                        className={
                          r.status?.toLowerCase() === 'finalized'
                            ? 'text-emerald-700'
                            : 'text-amber-800'
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-neutral-600 truncate max-w-xs">{r.notes || '—'}</td>
                    <td className="py-2 pr-3 align-top">
                      {r.previewFiltersJson ? (
                        <button
                          type="button"
                          className="text-sky-700 hover:text-sky-900 underline text-left text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFiltersModalJson(r.previewFiltersJson!);
                          }}
                        >
                          Show filters
                        </button>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        title={
                          r.status?.toLowerCase() === 'draft'
                            ? 'Delete this draft run'
                            : 'Only draft runs can be deleted'
                        }
                        disabled={r.status?.toLowerCase() !== 'draft' || deletingId === r.id}
                        onClick={(e) => void handleDeleteRun(e, r)}
                        className="inline-flex items-center justify-center rounded border border-red-200 bg-white p-1.5 text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                      >
                        {deletingId === r.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedId != null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-list-modal-title"
          onClick={() => setSelectedId(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] min-h-0 flex flex-col border border-neutral-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-start justify-between gap-2 px-4 py-3 border-b border-neutral-200 bg-neutral-50/80 shrink-0">
              <h2 id="payroll-list-modal-title" className="text-base font-semibold text-neutral-900 pr-8">
                Payroll list — run #{selectedId}
                {selectedRun && (
                  <span className="text-neutral-500 font-normal text-sm block sm:inline sm:ml-2">
                    ({formatPayrollRunPeriodRow(selectedRun)})
                  </span>
                )}
              </h2>
              <div className="flex flex-wrap items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={() => navigate(`/reports/payroll-details?runId=${selectedId}`)}
                  className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border border-blue-200 text-blue-800 bg-blue-50 hover:bg-blue-100"
                  title="Open this run in the Payroll Details report"
                >
                  <ExternalLink className="w-4 h-4" aria-hidden />
                  Payroll Details report
                </button>
                <button
                  type="button"
                  onClick={() => void loadPreview(selectedId)}
                  disabled={loadingPreview}
                  className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingPreview ? 'animate-spin' : ''}`} />
                  Reload preview
                </button>
                <button
                  type="button"
                  onClick={() => runPayrollExport('csv')}
                  disabled={!canExportList || exportLoading !== null}
                  className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border border-neutral-300 text-neutral-800 hover:bg-neutral-50 disabled:opacity-40"
                  title="Download payroll list as CSV"
                >
                  {exportLoading === 'csv' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Table2 className="w-4 h-4" aria-hidden />
                  )}
                  CSV
                </button>
                <button
                  type="button"
                  onClick={() => runPayrollExport('xlsx')}
                  disabled={!canExportList || exportLoading !== null}
                  className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border border-neutral-300 text-neutral-800 hover:bg-neutral-50 disabled:opacity-40"
                  title="Download payroll list as Excel"
                >
                  {exportLoading === 'xlsx' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4" aria-hidden />
                  )}
                  Excel
                </button>
                <button
                  type="button"
                  onClick={() => runPayrollExport('pdf')}
                  disabled={!canExportList || exportLoading !== null}
                  className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border border-neutral-300 text-neutral-800 hover:bg-neutral-50 disabled:opacity-40"
                  title="Download payroll list as PDF"
                >
                  {exportLoading === 'pdf' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" aria-hidden />
                  )}
                  PDF
                </button>
                <button
                  type="button"
                  onClick={() => void runSickSspCsvExport()}
                  disabled={selectedId == null || exportLoading !== null}
                  className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100 disabled:opacity-40"
                  title="Certified sick pay lines for external payroll (STAT_SICK)"
                >
                  {exportLoading === 'ssp' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4" aria-hidden />
                  )}
                  Sick pay CSV
                </button>
                <button
                  type="button"
                  onClick={() => runEntitySplitCsvExport()}
                  disabled={!preview || exportLoading !== null}
                  className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border border-neutral-300 text-neutral-800 hover:bg-neutral-50 disabled:opacity-40"
                  title="Export one CSV per main location (entity partition)"
                >
                  {exportLoading === 'entity' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Building2 className="w-4 h-4" aria-hidden />
                  )}
                  By location
                </button>
                <button
                  type="button"
                  onClick={() => void loadVariance()}
                  disabled={selectedId == null || varianceLoading}
                  className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border border-blue-200 text-blue-800 bg-blue-50 hover:bg-blue-100 disabled:opacity-40"
                  title="Projected (prior finalized) vs actual (current preview)"
                >
                  {varianceLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                  )}
                  Variance
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="inline-flex items-center justify-center rounded-lg border border-neutral-200 bg-white p-2 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                  aria-label="Close payroll list"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-4 space-y-4">

          {previewError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{previewError}</div>
          )}
          {finalizeMessage && (
            <div className="text-sm text-neutral-700 bg-neutral-100 border border-neutral-200 rounded px-3 py-2">
              {finalizeMessage}
            </div>
          )}
          {varianceSummary && (
            <div className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded px-3 py-2">
              {varianceSummary}
            </div>
          )}

          {selectedRun?.status?.toLowerCase() === 'finalized' && (
            <div className="text-sm text-sky-900 bg-sky-50 border border-sky-200 rounded px-3 py-2">
              This run is <strong>finalized</strong>
              {selectedRun.finalizedAt && (
                <> ({formatCompanyDateTime(selectedRun.finalizedAt)})</>
              )}
              . Preview is read-only; create a new draft run for another period.
            </div>
          )}

          {loadingPreview && !preview ? (
            <div className="flex items-center gap-2 text-neutral-500 text-sm py-6">
              <Loader2 className="w-5 h-5 animate-spin" />
              {selectedRun?.status?.toLowerCase() === 'finalized'
                ? 'Loading saved payroll…'
                : 'Computing gross…'}
            </div>
          ) : preview ? (
            <>
              <div className="flex flex-wrap gap-3 items-center">
                {selectedRun?.status?.toLowerCase() === 'finalized' ? (
                  <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" /> Finalized
                  </span>
                ) : preview.canFinalize ? (
                  <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" /> Ready to finalize
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4" /> Blocked
                  </span>
                )}
                {preview.hasHourlyMismatchBlocker && (
                  <span className="text-xs bg-amber-100 text-amber-900 px-2 py-0.5 rounded">
                    PD-010 hourly mismatch
                  </span>
                )}
                {preview.hasCalculationErrors && (
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Calculation errors</span>
                )}
              </div>

              {preview.finalizeBlockers.length > 0 && selectedRun?.status?.toLowerCase() !== 'finalized' && (
                <ul className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2 list-disc list-inside space-y-1">
                  {preview.finalizeBlockers.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}

              {filteredPreviewEmployees.some(
                (e) =>
                  filterPayrollRunWarnings(
                    e.warnings || [],
                    selectedRun?.status?.toLowerCase() === 'draft'
                  ).length > 0
              ) && (
                <div className="text-sm text-sky-950 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 flex gap-2">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 text-sky-700" aria-hidden />
                  <div>
                    <p className="font-medium text-sky-950">
                      Payroll warnings on{' '}
                      {
                        filteredPreviewEmployees.filter(
                          (e) =>
                            filterPayrollRunWarnings(
                              e.warnings || [],
                              selectedRun?.status?.toLowerCase() === 'draft'
                            ).length > 0
                        ).length
                      }{' '}
                      employee(s)
                      {filteredPreviewEmployees.length !== preview.employees.length ? (
                        <span className="font-normal text-sky-900/80">
                          {' '}
                          (filtered view — {preview.employees.length} total in run)
                        </span>
                      ) : null}
                    </p>
                    <p className="text-sky-900/90 mt-1 text-xs leading-relaxed">
                      Expand a row to read full messages. Missing pay-matrix schedule rates are highlighted before
                      finalize.
                    </p>
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-neutral-200 bg-neutral-50/60 p-3 space-y-2">
                <p className="text-xs font-medium text-neutral-700 flex items-center gap-1">
                  <Filter className="w-3.5 h-3.5" aria-hidden />
                  Preview filters
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-neutral-600">Schedule</label>
                    <select
                      value={scheduleId === '' ? '' : String(scheduleId)}
                      onChange={(e) => setScheduleId(e.target.value === '' ? '' : Number(e.target.value))}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-xs bg-white"
                    >
                      <option value="">All schedules</option>
                      {schedules.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-neutral-600">Position</label>
                    <select
                      value={positionId === '' ? '' : String(positionId)}
                      onChange={(e) => setPositionId(e.target.value === '' ? '' : Number(e.target.value))}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-xs bg-white"
                    >
                      <option value="">All positions</option>
                      {positions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1 sm:col-span-2 lg:col-span-1">
                    {employeeMasterLoading ? (
                      <p className="text-xs text-neutral-500 flex items-center gap-1.5 py-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading employees…
                      </p>
                    ) : (
                      <BasketSelect
                        label="Employees"
                        availableItems={employees.map((e) => ({
                          id: e.id,
                          label: `${e.firstName} ${e.lastName}${e.isActive ? '' : ' (Inactive)'}`.trim(),
                        }))}
                        selectedIds={selectedEmployeeIds}
                        onChange={(ids) =>
                          setSelectedEmployeeIds(ids.map((id) => (typeof id === 'string' ? Number(id) : id)))
                        }
                        placeholder="All employees"
                      />
                    )}
                  </div>
                </div>
              </div>

              {selectedRun?.status?.toLowerCase() === 'draft' && (
                <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-neutral-100">
                  {canForce && preview.hasHourlyMismatchBlocker && !preview.hasCalculationErrors && (
                    <label className="inline-flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={forceFinalize}
                        onChange={(ev) => setForceFinalize(ev.target.checked)}
                      />
                      Force finalize (PD-010 override — audited)
                    </label>
                  )}
                  <button
                    type="button"
                    disabled={
                      finalizing ||
                      (!preview.canFinalize &&
                        !(forceFinalize && canForce && preview.hasHourlyMismatchBlocker && !preview.hasCalculationErrors))
                    }
                    onClick={() => void handleFinalize()}
                    className="px-4 py-2 bg-emerald-700 text-white text-sm rounded-md hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {finalizing ? 'Finalizing…' : 'Finalize run'}
                  </button>
                </div>
              )}

              <div className="space-y-2 pt-2">
                <h3 className="text-sm font-medium text-neutral-700">
                  Employees ({filteredPreviewEmployees.length}
                  {filteredPreviewEmployees.length !== preview.employees.length
                    ? ` of ${preview.employees.length}`
                    : ''}
                  )
                </h3>
                {filteredPreviewEmployees.length === 0 ? (
                  <p className="text-xs text-neutral-500">No employees match the current filters.</p>
                ) : (
                  filteredPreviewEmployees.map((emp) => (
                    <EmployeePreviewRow
                      key={emp.employeeId}
                      emp={emp}
                      isDraftPreview={selectedRun?.status?.toLowerCase() === 'draft'}
                      open={expanded[emp.employeeId] === true}
                      onToggle={() => toggleEmp(emp.employeeId)}
                    />
                  ))
                )}
              </div>
            </>
          ) : null}
            </div>
          </div>
        </div>
      )}

      {filtersModalJson != null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-filters-modal-title"
          onClick={() => setFiltersModalJson(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto p-5 border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="payroll-filters-modal-title" className="text-lg font-semibold text-neutral-900 mb-1">
              Preview filters
            </h2>
            <p className="text-xs text-neutral-500 mb-4">Values selected when this run was created (list preview only).</p>
            {(() => {
              const data = parseSavedFiltersJson(filtersModalJson);
              if (!data) {
                return (
                  <pre className="text-xs bg-neutral-50 border border-neutral-200 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words">
                    {filtersModalJson}
                  </pre>
                );
              }
              const dept =
                data.payrollDepartmentId == null
                  ? 'All departments'
                  : data.payrollDepartmentName || `Department #${data.payrollDepartmentId}`;
              const loc =
                data.mainLocationId == null
                  ? 'All locations'
                  : data.mainLocationName || `Location #${data.mainLocationId}`;
              const pf =
                data.paymentFrequencyId == null
                  ? 'Any'
                  : data.paymentFrequencyName || `Frequency #${data.paymentFrequencyId}`;
              const payCat = payCategoryDisplay(data.payCategoryFilter ?? 'both');
              const sch =
                data.scheduleId == null
                  ? 'All schedules'
                  : data.scheduleName || `Schedule #${data.scheduleId}`;
              const pos =
                data.positionId == null
                  ? 'All positions'
                  : data.positionName || `Position #${data.positionId}`;
              const empCount = data.employeeIds?.length ?? 0;
              const empLabel =
                empCount === 0
                  ? 'All employees'
                  : `${empCount} selected employee${empCount === 1 ? '' : 's'}`;
              const rows: { label: string; value: string }[] = [
                { label: 'Period type', value: freqKindDisplay(data.freqKind) },
                { label: 'Calendar selection', value: data.periodSelectionLabel?.trim() || '—' },
                { label: 'Pay category', value: payCat },
                { label: 'Payroll department', value: dept },
                { label: 'Main location', value: loc },
                { label: 'Schedule', value: sch },
                { label: 'Position', value: pos },
                { label: 'Employees', value: empLabel },
                { label: 'Pay frequency (preview)', value: pf },
              ];
              return (
                <dl className="space-y-3 text-sm">
                  {rows.map((row) => (
                    <div key={row.label}>
                      <dt className="text-neutral-500 text-xs font-medium uppercase tracking-wide">{row.label}</dt>
                      <dd className="text-neutral-900 mt-0.5">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              );
            })()}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setFiltersModalJson(null)}
                className="px-4 py-2 bg-neutral-900 text-white text-sm rounded-md hover:bg-neutral-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function EmployeePreviewRow({
  emp,
  isDraftPreview,
  open,
  onToggle,
}: {
  emp: EmployeePayrollCalculationResultDto;
  isDraftPreview: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const visibleWarnings = filterPayrollRunWarnings(emp.warnings || [], isDraftPreview);
  return (
    <div className="border border-neutral-200 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 hover:bg-neutral-100 text-left text-sm"
      >
        <span className="font-medium text-neutral-800">
          {emp.employeeName}{' '}
          <span className="font-normal text-neutral-500">#{emp.employeeId}</span>
          {emp.isEmployeeActive === false && (
            <span className="ml-1.5 text-[10px] uppercase tracking-wide font-semibold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded align-middle">
              Inactive
            </span>
          )}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {!emp.success && <span className="text-xs text-red-600">Errors</span>}
          {visibleWarnings.length > 0 && (
            <span className="text-xs text-sky-800 bg-sky-100 px-1.5 py-0.5 rounded">
              {visibleWarnings.length} warning{visibleWarnings.length === 1 ? '' : 's'}
            </span>
          )}
          {emp.hourlyRateMismatchBlocksFinalize && (
            <span className="text-xs text-amber-700">PD-010</span>
          )}
          <span className="font-mono text-neutral-700">€{emp.grossTotal.toFixed(2)}</span>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-3 py-2 text-sm border-t border-neutral-200 bg-white space-y-2">
          {emp.errors?.length > 0 && (
            <div className="text-red-700 text-xs space-y-0.5">
              {emp.errors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </div>
          )}
          {visibleWarnings.length > 0 && (
            <div className="rounded-md border border-sky-200 bg-sky-50/80 px-2 py-2">
              <p className="text-[11px] font-medium text-sky-900 mb-1">Warnings</p>
              <ul className="text-xs text-sky-950 space-y-1 list-disc list-inside">
                {visibleWarnings.map((w, i) => (
                  <li key={i} className="break-words">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <LinesTable lines={emp.lines} derivedHourlyBase={emp.derivedHourlyBase} />
        </div>
      )}
    </div>
  );
}

function formatLineCategory(category: string): string {
  if (category === 'TerminationLeavePayout') return 'Termination leave (PD-017)';
  if (category === 'TimecardExtras') return 'Extras';
  if (category === 'SundayPremium') return 'Sunday premium';
  if (category === 'BankHolidayPay') return 'Holiday premium';
  if (category === 'MatrixRate') return 'Pay matrix (by schedule)';
  if (category === 'WeekSubtotal') return 'Week subtotal';
  if (category === 'NurseBandWorked') return 'Worked (nurse banding)';
  if (category === 'NurseBandAlAccrual') return 'Annual leave accrual';
  if (category === 'NurseBandBhStatutory') return 'Bank holiday statutory';
  return category;
}

function formatAlAccrualRate(hourlyRateApplied: number, derivedHourlyBase?: number | null): string {
  const rate = Number(hourlyRateApplied);
  if (rate > 0) return rate.toFixed(2);
  const base = Number(derivedHourlyBase);
  if (base > 0) return base.toFixed(2);
  return '—';
}

function LinesTable({
  lines,
  derivedHourlyBase,
}: {
  lines: PayrollGrossLineDto[];
  derivedHourlyBase?: number | null;
}) {
  if (!lines?.length) return <p className="text-xs text-neutral-500">No lines.</p>;
  const sorted = sortPayrollPreviewLines(lines);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-neutral-500 border-b border-neutral-200">
            <th className="pb-1 pr-2">Category</th>
            <th className="pb-1 pr-2">Date</th>
            <th className="pb-1 pr-2">Hours</th>
            <th className="pb-1 pr-2">Rate</th>
            <th className="pb-1 pr-2">Amount</th>
            <th className="pb-1">Detail</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((ln, i) => {
            const amount = displayLineAmount(ln);
            const isWeekSubtotal = ln.category === 'WeekSubtotal';
            const isAlAccrual = ln.category === 'NurseBandAlAccrual';
            const weekTotals = isWeekSubtotal ? weekSubtotalTotals(sorted, ln.date) : null;
            return (
              <tr
                key={i}
                className={`border-b border-neutral-100 ${isWeekSubtotal ? 'bg-neutral-50 font-medium' : ''}`}
              >
                <td className="py-1 pr-2 text-neutral-800">{formatLineCategory(ln.category)}</td>
                <td className="py-1 pr-2 text-neutral-600">{ln.date ? formatYmd(ln.date) : '—'}</td>
                <td className="py-1 pr-2 font-mono">
                  {isWeekSubtotal ? weekTotals!.hours.toFixed(2) : Number(ln.hours).toFixed(2)}
                </td>
                <td className="py-1 pr-2 font-mono text-neutral-400">
                  {isWeekSubtotal
                    ? '—'
                    : isAlAccrual
                      ? formatAlAccrualRate(ln.hourlyRateApplied, derivedHourlyBase)
                      : Number(ln.hourlyRateApplied).toFixed(2)}
                </td>
                <td className="py-1 pr-2 font-mono">
                  {isWeekSubtotal ? weekTotals!.amount.toFixed(2) : amount.toFixed(2)}
                </td>
                <td className="py-1 text-neutral-600">
                  {ln.category === 'NurseBandWorked'
                    ? formatPayrollLineDetail(ln)
                    : ln.detail || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default PayrollRunsPage;
